"use client";

import { getRecaptchaSiteKey } from "@/constants/appConstants";
import { useProfile } from "@/context/useProfileProps";
import { useRecaptchaV3 } from "@/hooks";
import { ModalLoadingTypes } from "@/types/common";
import { getSiteProps } from "@/utils";
import React, { useEffect, useLayoutEffect, useState } from "react";

import ProfileModal from "@/components/common/profileModal";
import { useSendMobileOTP } from "@/components/profile/contactCard/query";
import { MobileNumberFlowWrapperProps } from "@/components/profile/contactCard/types";

import ChangeMobileNumber from "./ChangeMobileNumber";
import MobileOTP from "./MobileOTP";

const MobileNumberFlowWrapper = ({
  isEdit,
  closeEdit,
  type,
  mobileNumber,
  countryCode,
  formRef,
  otpRef,
  isCalledFromOutside = false,
  handleFooterBtnLoading,
}: MobileNumberFlowWrapperProps) => {
  const siteConfig = getSiteProps(window.location.hostname);
  const isFromRexmonster = siteConfig?.site_map === "rexmonster";
  const {
    isMobile,
    translation: { errorMessages },
    showToast,
    hostname,
  } = useProfile();
  const search = window.location.search;
  let urlParams = new URLSearchParams(search);
  const isApp = urlParams.get("app");
  const { mutate: sendMobileOTP } = useSendMobileOTP();
  const [loading, setLoading] = useState({
    isSaveLoading: false,
    isDeleteLoading: false,
  });

  const [mobileNumberData, setMobileNumberData] = useState(mobileNumber);
  const [countryCodeData, setCountryCodeData] = useState(countryCode);
  const [mobileNumberFlowType, setMobileNumberFlowType] = useState(type);

  const { executeRecaptcha } = useRecaptchaV3(
    getRecaptchaSiteKey(hostname),
    true,
  );

  const [otpId, setOtpId] = useState("");

  const handleLoading = (e: ModalLoadingTypes) => {
    setLoading((prev) => ({ ...prev, ...e }));
    if (e.isSaveLoading !== undefined && handleFooterBtnLoading) {
      handleFooterBtnLoading({ isSaveLoading: e.isSaveLoading });
    }
  };

  const sendMobileOTPHandler = async (
    mobileNumber: string,
    countryCode: string,
  ) => {
    handleLoading({ isSaveLoading: true });
    const { reCaptchaToken, reCaptchaAction } =
      await executeRecaptcha("sendotp");

    const apiData = {
      username: `+${countryCode}${mobileNumber}`,
      reCaptchaToken,
      reCaptchaAction,
    };

    // api call to send mobile otp
    sendMobileOTP(apiData, {
      onSuccess: (data) => {
        setOtpId(data?.id);
        handleLoading({ isSaveLoading: false });
        showToast({
          show: true,
          type: "success",
          message: "OTP sent successfully",
        });
      },
      onError: (err) => {
        handleLoading({ isSaveLoading: false });
        showToast({
          show: true,
          type: "error",
          message:
            err instanceof Error
              ? err.message
              : errorMessages.somethingWentWrong,
        });
      },
    });
  };

  // userLayoutEffect is used to send mobile otp before rendering the component
  useLayoutEffect(() => {
    if (
      type === "VERIFY_MOBILE_NUMBER" &&
      mobileNumber &&
      countryCode &&
      isEdit
    ) {
      sendMobileOTPHandler(mobileNumber, countryCode);
    }
  }, [isEdit, type]);

  useEffect(() => {
    setMobileNumberFlowType(type);
  }, [type]);

  const updateMobileNumberAndCountryCode = (
    mobileNumber: string,
    countryCode: string,
  ) => {
    setMobileNumberData(mobileNumber);
    setCountryCodeData(countryCode);
  };

  const renderMobileNumberFlow = () => {
    switch (mobileNumberFlowType) {
      case "ADD_MOBILE_NUMBER":
        return (
          <ChangeMobileNumber
            type="ADD_MOBILE_NUMBER"
            showMobileModal={isEdit}
            closeMobileModal={closeEdit}
            updateOTPIdHandler={(optId: string) => setOtpId(optId)}
            formRef={formRef}
            setLoading={(value: boolean) =>
              handleLoading({ isSaveLoading: value })
            }
            changeMobileNumberType={(
              mobileNumber: string,
              countryCode: string,
            ) => {
              updateMobileNumberAndCountryCode(mobileNumber, countryCode);
              setMobileNumberFlowType("VERIFY_MOBILE_NUMBER");
            }}
            executeRecaptcha={executeRecaptcha}
          />
        );
      case "CHANGE_MOBILE_MOBILE":
        return (
          <ChangeMobileNumber
            type="CHANGE_MOBILE_MOBILE"
            showMobileModal={isEdit}
            closeMobileModal={closeEdit}
            mobileNumber={mobileNumberData}
            countryCode={countryCodeData}
            updateOTPIdHandler={(optId: string) => setOtpId(optId)}
            changeMobileNumberType={(
              mobileNumber: string,
              countryCode: string,
            ) => {
              updateMobileNumberAndCountryCode(mobileNumber, countryCode);
              setMobileNumberFlowType("VERIFY_MOBILE_NUMBER");
            }}
            formRef={formRef}
            setLoading={(value: boolean) =>
              handleLoading({ isSaveLoading: value })
            }
            executeRecaptcha={executeRecaptcha}
          />
        );
      case "VERIFY_MOBILE_NUMBER":
        if (!mobileNumberData || !countryCodeData) return null;
        return (
          <MobileOTP
            showMobileModal={isEdit}
            closeMobileModal={closeEdit}
            mobileNumber={mobileNumberData}
            countryCode={countryCodeData}
            otpId={otpId}
            changeMobileNumberType={() =>
              setMobileNumberFlowType("CHANGE_MOBILE_MOBILE")
            }
            otpRef={otpRef}
            setLoading={(value: boolean) =>
              handleLoading({ isSaveLoading: value })
            }
            executeRecaptcha={executeRecaptcha}
          />
        );
      default:
        return (
          <ChangeMobileNumber
            type="ADD_MOBILE_NUMBER"
            showMobileModal={isEdit}
            closeMobileModal={closeEdit}
            mobileNumber={mobileNumberData}
            countryCode={countryCodeData}
            updateOTPIdHandler={(optId: string) => setOtpId(optId)}
            changeMobileNumberType={(
              mobileNumber: string,
              countryCode: string,
            ) => {
              updateMobileNumberAndCountryCode(mobileNumber, countryCode);
              setMobileNumberFlowType("VERIFY_MOBILE_NUMBER");
            }}
            formRef={formRef}
            setLoading={(value: boolean) =>
              handleLoading({ isSaveLoading: value })
            }
            executeRecaptcha={executeRecaptcha}
          />
        );
    }
  };

  return !isCalledFromOutside ? (
    <ProfileModal
      showModal={isEdit}
      closeModal={() => {
        // reset the mobile number states
        setMobileNumberFlowType(type);
        setMobileNumberData(mobileNumber);
        setCountryCodeData(countryCode);

        closeEdit();
      }}
      showCloseButton={true}
      isMobile={isMobile}
      screenAlignment={isMobile ? "bottom-sheet" : "center"}
      heading={
        mobileNumberFlowType === "ADD_MOBILE_NUMBER"
          ? "Add phone number"
          : mobileNumberFlowType === "CHANGE_MOBILE_MOBILE"
            ? "Change phone Number"
            : "Verify phone number"
      }
      footerPrimaryBtnLabel={
        mobileNumberFlowType === "ADD_MOBILE_NUMBER"
          ? isFromRexmonster
            ? "Add & Verify"
            : "Add"
          : mobileNumberFlowType === "CHANGE_MOBILE_MOBILE"
            ? isFromRexmonster
              ? "Save & Verify"
              : "Save"
            : "Verify"
      }
      footerPrimaryBtnHandler={() => {
        if (
          mobileNumberFlowType === "ADD_MOBILE_NUMBER" ||
          mobileNumberFlowType === "CHANGE_MOBILE_MOBILE"
        ) {
          formRef.current?.requestSubmit();
        } else {
          otpRef.current?.verifyOTPHandler();
        }
      }}
      footerPrimaryBtnIsLoading={loading.isSaveLoading}
      customClass={isMobile && isApp ? "h-[70vh]" : ""}
    >
      {renderMobileNumberFlow()}
    </ProfileModal>
  ) : (
    renderMobileNumberFlow()
  );
};

export default MobileNumberFlowWrapper;
